// src/pages/MyContainers.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  toDisplayDate,
  toDisplayDateTime,
  normalizeDateInput,
} from "../utils/dataFormatters";
import { getCurrentUser } from "../utils/auth";
import { filterRowsByAccess } from "../utils/accessFilters";
import { usePermissions } from "../utils/permissions";
import DateRangeFilter from "../components/common/DateRangeFilter";
import ConfirmModal from "../components/ConfirmModal";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";

/* ========================= 基本設定 ========================= */

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

/** 欄位定義（比照 Excel） */
const COLUMNS = [
  { key: "order_no",      label: "Order No",           width: 180 },
  { key: "cust_code",     label: "Cust Code",          width: 110 },
  { key: "status",        label: "Status",             width: 110 }, // 唯讀、由程式自動算
  { key: "m_closed",      label: "M-Closed",           width: 110, type: "boolean" },
  { key: "drayage",       label: "Drayage",            width: 110 },
  { key: "warehouse",     label: "Warehouse",          width: 110 },
  { key: "mbl_no",        label: "MBL No",             width: 150 },
  { key: "container",     label: "Container No",       width: 140 },
  { key: "etd",           label: "ETD",                width: 110, type: "date" },
  { key: "eta",           label: "ETA",                width: 110, type: "date" },
  { key: "pod",           label: "POD",                width: 150 },
  { key: "arrived",       label: "Arrived",            width: 110, type: "date" },
  { key: "lfd",           label: "LFD",                width: 110, type: "date" },
  { key: "appt_date",     label: "Appt Date",          width: 130, type: "date" },
  { key: "lrd",           label: "LRD",                width: 110, type: "date" },
  { key: "delivered_dt",  label: "Delivered DateTime", width: 180, type: "datetime" },
  { key: "emptied_dt",    label: "Emptied DateTime",   width: 170, type: "datetime" },
  { key: "returned_date", label: "Returned Date",      width: 130, type: "date" },
];

const ORDER_NO_INDEX = COLUMNS.findIndex((col) => col.key === "order_no");
const ILS_IB_COLUMN_KEY = "ils_ib_code";
const ILS_IB_COLUMN = { key: ILS_IB_COLUMN_KEY, label: "iLS IB Code", width: 180 };
const TABLE_COLUMNS =
  ORDER_NO_INDEX === -1
    ? [ILS_IB_COLUMN, ...COLUMNS]
    : [
        ...COLUMNS.slice(0, ORDER_NO_INDEX + 1),
        ILS_IB_COLUMN,
        ...COLUMNS.slice(ORDER_NO_INDEX + 1),
      ];

const IB_STATES = {
  PENDING: "pending",
  READY: "ready",
  FETCHING: "fetching",
  FOUND: "found",
  NOT_FOUND: "not_found",
  ERROR: "error",
};
const IB_FILTER_READY = "__IB_READY__";
const IB_FILTER_PENDING = "__IB_PENDING__";
const BLANK_CUST_OPTION = "__BLANK_CUST__";
const BLANK_DRAYAGE_OPTION = "__BLANK_DRAYAGE__";
const INTRANSIT_DRILL_DEPART = "mrp_intransit_depart";
const INTRANSIT_DRILL_ARRIVAL = "mrp_intransit_arrival";
const INTRANSIT_DRILL_TYPES = new Set([INTRANSIT_DRILL_DEPART, INTRANSIT_DRILL_ARRIVAL]);
const INTRANSIT_TAB_SUMMARY = "summary";
const INTRANSIT_TAB_DETAILS = "details";

const IB_BADGE_STYLES = {
  [IB_STATES.PENDING]:   { label: "Pending", className: "bg-gray-200 text-gray-700 border border-gray-300" },
  [IB_STATES.READY]:     { label: "Ready", className: "bg-blue-100 text-blue-700 border border-blue-300" },
  [IB_STATES.FETCHING]:  { label: "Fetching...", className: "bg-yellow-100 text-yellow-700 border border-yellow-300" },
  [IB_STATES.FOUND]:     { label: "Found", className: "bg-emerald-100 text-emerald-700 border border-emerald-300" },
  [IB_STATES.NOT_FOUND]: { label: "Not Found", className: "bg-orange-100 text-orange-700 border border-orange-300" },
  [IB_STATES.ERROR]:     { label: "Error", className: "bg-red-100 text-red-700 border border-red-300" },
};

const IB_FETCHABLE_STATES = new Set([IB_STATES.READY, IB_STATES.ERROR, IB_STATES.NOT_FOUND]);
const ILS_FETCH_DELAY_MS = 200;

const DATE_FIELDS     = new Set(COLUMNS.filter(c => c.type === "date").map(c => c.key));
const DATETIME_FIELDS = new Set(COLUMNS.filter(c => c.type === "datetime").map(c => c.key));
const ACCESS_COLUMN_HINTS = [
  ...COLUMNS.map((column) => column.key),
  "order_customerinvoicecode",
  "cust_code",
  "customer_code",
  "customer",
  "company_code",
  "warehouse",
  "whse_code",
];
const WIDTHS_KEY      = "mycontainers.colwidths";
const BLANK_WAREHOUSE_OPTION = "__BLANK__";
const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatQtyValue = (value) =>
  toFiniteNumber(value).toLocaleString(undefined, { maximumFractionDigits: 2 });

function parseInTransitDrillParams() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search || "");
  const drill = String(params.get("drill") || "").trim().toLowerCase();
  if (!INTRANSIT_DRILL_TYPES.has(drill)) return null;

  const dateField = String(params.get("date_field") || "").trim().toLowerCase();
  if (dateField !== "etd" && dateField !== "arrival_effective_date") return null;

  const dateFrom = String(params.get("date_from") || "").trim();
  const dateTo = String(params.get("date_to") || "").trim();
  if (!dateFrom || !dateTo) return null;

  const whseCodeRaw = String(params.get("whse_code") || "ALL").trim();
  const whseCode = whseCodeRaw ? whseCodeRaw.toUpperCase() : "ALL";
  const whseCodesRaw = String(params.get("whse_codes") || "").trim();
  const whseCodes = whseCodesRaw
    ? Array.from(
        new Set(
          whseCodesRaw
            .split(",")
            .map((token) => String(token || "").trim().toUpperCase())
            .filter((token) => token && token !== "ALL")
        )
      )
    : [];
  const walmartItemNo = String(params.get("walmart_item_no") || "").trim();
  const cellValueRaw = String(params.get("cell_value") || "").trim();
  const parsedCellValue = Number(cellValueRaw);

  return {
    drill,
    dateField,
    dateFrom,
    dateTo,
    whseCode,
    whseCodes,
    walmartItemNo,
    hasCellValue: Number.isFinite(parsedCellValue),
    cellValue: Number.isFinite(parsedCellValue) ? parsedCellValue : 0,
  };
}

/* 只比較資料欄位用的快照（避免把 UI 狀態算進 dirty） */
const CANON_KEYS = [...new Set([...COLUMNS.map(c => c.key), ILS_IB_COLUMN_KEY, "status", "_id"])];
function rowsSnapshot(rows = []) {
  const arr = rows.map(r => {
    const o = {};
    CANON_KEYS.forEach(k => { o[k] = r?.[k] ?? ""; });
    return o;
  });
  arr.sort((a, b) => String(a._id).localeCompare(String(b._id)));
  return JSON.stringify(arr);
}

/* 對外層守門員同步的 localStorage 鍵（兩個都同步，提升相容性） */
const DIRTY_KEYS = ["pp_unsaved_mycontainers", "pp_dirty"];
function mirrorDirtyToStorage(flag) {
  try {
    DIRTY_KEYS.forEach(k => flag ? localStorage.setItem(k, "1")
                                 : localStorage.removeItem(k));
  } catch {}
}

const makeIbEntry = (overrides = {}) => ({
  state: IB_STATES.PENDING,
  hash: "",
  code: "",
  error: "",
  reason: "",
  ...overrides,
});

const upperTrim = (value) => String(value || "").trim().toUpperCase();
const delay = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));

/* ========================= 小工具/樣式 ========================= */

// 產生縮寫（沿用 UserMenu 規則）
function initialsFrom(name, email) {
  const local = (email || "").split("@")[0] || "";
  const src = (name || "").trim() || local || "user";
  const tokens = src.split(/[^A-Za-z0-9]+/).filter(Boolean);
  let a = "", b = "";
  if (tokens.length >= 2) {
    a = tokens[0][0];
    b = tokens[tokens.length - 1][0];
  } else {
    const t = tokens[0] || local || "US";
    a = t[0] || "U";
    const m = t.match(/[A-Za-z0-9](?=[^A-Za-z0-9]*$)/);
    b = (m && m[0]) || local[1] || local[0] || "S";
  }
  return (String(a) + String(b)).toUpperCase();
}

function CurrentUserBadge() {
  const me = getCurrentUser();
  if (!me) return null;
  const initials = initialsFrom(me.name, me.email);
  const role = String(me.role || "").trim();
  return (
    <div className="ml-3 flex items-center gap-2 text-xs" title={`${me.name || me.email} • ${role || "—"}`}>
      <div className="h-6 w-6 rounded-full bg-teal-500 text-white grid place-items-center font-bold">{initials}</div>
      <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700">{role || "—"}</span>
    </div>
  );
}

// 欄位 key（給權限矩陣用）
const FIELD_KEYS = Object.fromEntries(COLUMNS.map(c => [c.key, c.key]));

// 權限
const PERMS = {
  overview: (key) => (key === FIELD_KEYS.status ? "auto" : "read"),
  admin: (key) => (key === FIELD_KEYS.status ? "auto" : "edit"),
  dispatcher: (key) => ({
    [FIELD_KEYS.order_no]: "read",
    [FIELD_KEYS.status]:   "auto",
    [FIELD_KEYS.m_closed]:"edit",
    [FIELD_KEYS.drayage]:  "edit",
    [FIELD_KEYS.warehouse]:"edit",
    [FIELD_KEYS.mbl_no]:   "edit",
    [FIELD_KEYS.container]:"edit",
    [FIELD_KEYS.etd]:      "edit",
    [FIELD_KEYS.eta]:      "edit",
    [FIELD_KEYS.pod]:      "edit",
    [FIELD_KEYS.arrived]:  "read",
    [FIELD_KEYS.lfd]:      "read",
    [FIELD_KEYS.appt_date]:"read",
    [FIELD_KEYS.lrd]:      "read",
    [FIELD_KEYS.delivered_dt]:"read",
    [FIELD_KEYS.emptied_dt]  :"read",
    [FIELD_KEYS.returned_date]:"read",
  }[key] || "read"),
  drayage: (key) => ({
    [FIELD_KEYS.order_no]: "read",
    [FIELD_KEYS.status]:   "auto",
    [FIELD_KEYS.m_closed]:"edit",
    [FIELD_KEYS.drayage]:  "read",
    [FIELD_KEYS.warehouse]:"read",
    [FIELD_KEYS.mbl_no]:   "read",
    [FIELD_KEYS.container]:"read",
    [FIELD_KEYS.etd]:      "read",
    [FIELD_KEYS.eta]:      "read",
    [FIELD_KEYS.pod]:      "read",
    [FIELD_KEYS.arrived]:  "edit",
    [FIELD_KEYS.lfd]:      "edit",
    [FIELD_KEYS.appt_date]:"edit",
    [FIELD_KEYS.lrd]:      "edit",
    [FIELD_KEYS.delivered_dt]:"edit",
    [FIELD_KEYS.emptied_dt]  :"read",
    [FIELD_KEYS.returned_date]:"edit",
  }[key] || "read"),
  warehouse: (key) => ({
    [FIELD_KEYS.order_no]: "read",
    [FIELD_KEYS.status]:   "auto",
    [FIELD_KEYS.m_closed]:"edit",
    [FIELD_KEYS.drayage]:  "read",
    [FIELD_KEYS.warehouse]:"read",
    [FIELD_KEYS.mbl_no]:   "read",
    [FIELD_KEYS.container]:"read",
    [FIELD_KEYS.etd]:      "read",
    [FIELD_KEYS.eta]:      "read",
    [FIELD_KEYS.pod]:      "read",
    [FIELD_KEYS.arrived]:  "read",
    [FIELD_KEYS.lfd]:      "read",
    [FIELD_KEYS.lrd]:      "read",
    [FIELD_KEYS.appt_date]:"read",
    [FIELD_KEYS.delivered_dt]:"read",
    [FIELD_KEYS.emptied_dt]  :"edit",
    [FIELD_KEYS.returned_date]:"read",
  }[key] || "read"),
};

const FEATURE_FLAGS_BY_ROLE = {
  admin: { addRow: true, importData: true, exportData: true, deleteRows: true, saveChanges: true, getIbCode: true },
  dispatcher: { addRow: true, importData: true, exportData: true, deleteRows: true, saveChanges: true, getIbCode: true },
  drayage: { addRow: false, importData: false, exportData: true, deleteRows: false, saveChanges: true, getIbCode: true },
  warehouse: { addRow: false, importData: false, exportData: true, deleteRows: false, saveChanges: true, getIbCode: true },
  overview: { addRow: false, importData: false, exportData: false, deleteRows: false, saveChanges: false, getIbCode: false },
  default: { addRow: false, importData: false, exportData: false, deleteRows: false, saveChanges: false, getIbCode: false },
};



/** 影響 Status 欄位 */
const AFFECTS_STATUS = new Set([
  "returned_date", "emptied_dt", "delivered_dt", "appt_date", "arrived",
  "mbl_no", "container", "m_closed", "status_code",
]);

/** Status 樣式 */
const STATUS_STYLES = {
  Booked:     "bg-cyan-100 text-cyan-700 border border-cyan-200",
  Planning:   "bg-gray-100 text-gray-700 border border-gray-200",
  Offshore:   "bg-blue-100 text-blue-700 border border-blue-200",
  Arrival:    "bg-amber-100 text-amber-700 border border-amber-200",
  "Appt Made":"bg-purple-100 text-purple-700 border border-purple-200",
  Delivered:  "bg-green-100 text-green-700 border border-green-200",
  Emptied:    "bg-violet-100 text-violet-700 border border-violet-200",
  Closed:     "bg-red-100 text-red-700 border border-red-200",
};
const STATUS_FILTER_OPTIONS = Object.keys(STATUS_STYLES).map((status) => ({
  value: status,
  label: status,
}));

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (!v) return false;
    return ["1", "true", "t", "yes", "y", "on"].includes(v);
  }
  return false;
};

/** 計算 Status */
function computeStatus(r = {}) {
  const manual = String(r?.status ?? "").trim();
  if (manual === "Returned" || manual === "Closed") return "Closed";
  if (toBool(r?.m_closed)) return "Closed";
  const has = (k) => !!String(r?.[k] ?? "").trim();
  const rawCode = r?.status_code ?? r?.statusCode ?? r?.order_status ?? r?.orderStatus;
  const statusCode = (() => {
    if (rawCode === null || rawCode === undefined || rawCode === "") return null;
    const parsed = Number(String(rawCode).replace(/[^0-9-]/g, ""));
    return Number.isNaN(parsed) ? null : parsed;
  })();
  if (has("returned_date"))              return "Closed";
  if (has("emptied_dt"))                 return "Emptied";
  if (has("delivered_dt"))               return "Delivered";
  if (has("appt_date"))                  return "Appt Made";
  if (has("arrived"))                    return "Arrival";
  // Planning-stage mapping uses Wayli order_status (status_code).
  if (statusCode === 3)                  return "Offshore";
  if (statusCode === 2)                  return "Booked";
  if (statusCode === 1)                  return "Planning";
  return "Planning";
}

/** id/MBL 工具 */
const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const ensureUniqueIds = (arr = []) => {
  const seen = new Map();
  return arr.map((row) => {
    const base = row && row._id ? String(row._id) : "";
    const useId = base || genId();
    const count = seen.get(useId) || 0;
    seen.set(useId, count + 1);
    if (count === 0) return row && row._id ? row : { _id: useId, ...row };
    return { ...row, _id: `${useId}--dup-${count + 1}` };
  });
};
const normalizeOrderNo = (s) => String(s || "").trim().toLowerCase();

const normalizeRowsForState = (rows = []) =>
  ensureUniqueIds(rows).map((r) => {
    const next = { ...r, m_closed: toBool(r.m_closed) };
    if (r && Object.prototype.hasOwnProperty.call(r, ILS_IB_COLUMN_KEY)) {
      const value = r[ILS_IB_COLUMN_KEY];
      next[ILS_IB_COLUMN_KEY] = value == null ? "" : String(value).trim();
    }
    next.status = computeStatus(next);
    return next;
  });

function findDuplicateOrderNos(list) {
  const seen = new Map();
  const dups = new Set();
  for (const r of list) {
    const raw = r?.order_no;
    const k = normalizeOrderNo(raw);
    if (!k) continue;
    if (seen.has(k)) { dups.add(seen.get(k)); dups.add(raw); }
    else seen.set(k, raw);
  }
  return [...dups];
}

/** 狀態 badge */
function StatusBadge({ value }) {
  let display = value;
  if (typeof display === "string") {
    const lower = display.trim().toLowerCase();
    if (!lower) display = "Planning";
    else if (lower === "returned" || lower === "closed") display = "Closed";
    else display = display.trim();
  } else {
    display = "Planning";
  }
  const cls = STATUS_STYLES[display] || STATUS_STYLES.Planning;
  return <span className={`inline-block px-2 py-0.5 text-xs rounded ${cls}`}>{display}</span>;
}

function InboundCodeBadge({ entry, onCopy }) {
  const state = entry?.state || IB_STATES.PENDING;
  const config = IB_BADGE_STYLES[state] || IB_BADGE_STYLES[IB_STATES.PENDING];
  const defaultTitle =
    state === IB_STATES.ERROR
      ? entry?.error || entry?.reason || config.label
      : entry?.reason || config.label;
  const title = state === IB_STATES.FOUND ? "Copy" : defaultTitle;

  if (state === IB_STATES.FOUND && entry?.code) {
    return (
      <button
        type="button"
        className="w-full text-left"
        onClick={() => onCopy?.(entry.code)}
        title={title || "Click to copy"}
      >
        <span
          className={`inline-flex items-center rounded border px-2 py-0.5 text-sm font-medium ${config.className} cursor-pointer hover:opacity-80`}
        >
          {entry.code}
        </span>
      </button>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${config.className}`}
      title={title || config.label}
    >
      {state === IB_STATES.FETCHING ? "Fetching..." : config.label}
    </span>
  );
}

/** 以「日期」為粒度的 timestamp（忽略時間；datetime 取日期部分） */
function toDayTS(x) {
  if (!x) return NaN;
  const dOnly = normalizeDateInput(String(x), false);
  if (dOnly) {
    const t = new Date(dOnly + "T00:00:00").getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  const dt = normalizeDateInput(String(x), true);
  if (dt) {
    const datePart = dt.split(" ")[0];
    const t = new Date(datePart + "T00:00:00").getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  return NaN;
}

/* ========================= 主元件 ========================= */

export default function MyContainers() {
  const accessVersion = useAccessOverrideVersion();
  const [rows, setRows]               = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [toDeleteCount, setToDeleteCount] = useState(0);
  const [ibStatusMap, setIbStatusMap] = useState({});
  const [ibBatchState, setIbBatchState] = useState({ running: false, total: 0, processed: 0 });
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const drillParams = useMemo(() => parseInTransitDrillParams(), []);
  const [showWarehouseNA, setShowWarehouseNA] = useState(() =>
    drillParams ? drillParams.whseCode === "ALL" && !drillParams.whseCodes?.length : false
  );
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState("");
  const [breakdownTab, setBreakdownTab] = useState(INTRANSIT_TAB_SUMMARY);
  const [breakdownSummaryRows, setBreakdownSummaryRows] = useState([]);
  const [breakdownDetailRows, setBreakdownDetailRows] = useState([]);
  const [breakdownTotal, setBreakdownTotal] = useState(0);
  const accessFilteredRows = useMemo(() => {
    void accessVersion;
    return filterRowsByAccess(rows, {
      columns: ACCESS_COLUMN_HINTS,
      disableCust: true,
      whsKeys: ["warehouse", "whse_code"],
      allowBlank: false,
    });
  }, [rows, accessVersion]);
  const [loading, setLoading]         = useState(false);
  const [err, setErr]                 = useState("");
  const [sortKey, setSortKey]         = useState("");
  const [sortDir, setSortDir]         = useState(0); // 0 none / 1 asc / -1 desc
  const [widths, setWidths]           = useState(() => {
    let parsed = {};
    try {
      const saved = localStorage.getItem(WIDTHS_KEY);
      if (saved) parsed = JSON.parse(saved) || {};
    } catch {
      parsed = {};
    }
    TABLE_COLUMNS.forEach((col) => {
      if (typeof parsed[col.key] !== "number") {
        parsed[col.key] = col.width || 120;
      }
    });
    return parsed;
  });

  // 未儲存變更偵測：以 rows 快照 vs 已儲存快照
  const [savedSnap, setSavedSnap]   = useState("");
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);
  const hasUnsavedRef = useRef(false);
  useEffect(() => {
    hasUnsavedRef.current = hasUnsaved;
  }, [hasUnsaved]);

  // 逐欄位 filter 狀態
const createInitialFilters = () => ({
    order_no: "", status: [], m_closed: "", drayage: [], warehouse: [], ils_ib_code: [], cust_code: [], pod: [],
    mbl_no: "", container: "",
    etdFrom: "", etdTo: "", etaFrom: "", etaTo: "",
    arrivedFrom: "", arrivedTo: "", lfdFrom: "", lfdTo: "",
    appt_dateFrom: "", appt_dateTo: "", lrdFrom: "", lrdTo: "",
    delivered_dtFrom: "", delivered_dtTo: "",
    emptied_dtFrom: "", emptied_dtTo: "",
    returned_dateFrom: "", returned_dateTo: "",
  });
  const [filters, setFilters] = useState(() => createInitialFilters());
  const setFilter = (k, v) =>
    setFilters((f) => ({
      ...f,
      [k]: Array.isArray(v) ? [...v] : v,
    }));
  const resetFilters = () => setFilters(createInitialFilters());

  // 選單資料
  const [drayageCodes, setDrayageCodes] = useState([]);
  const [whseCodes, setWhseCodes] = useState([]);
  const drayageOptions = useMemo(() => {
    const seen = new Set();
    const mapped = drayageCodes
      .map((code) => String(code || "").trim())
      .filter(Boolean)
      .filter((code) => {
        const key = code.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((code) => ({ value: code.toLowerCase(), label: code }));
    const hasBlank = accessFilteredRows.some((row) => !String(row?.drayage || "").trim());
    return hasBlank ? [{ value: BLANK_DRAYAGE_OPTION, label: "(Blank)" }, ...mapped] : mapped;
  }, [drayageCodes, accessFilteredRows]);
  const buildValueOptions = useCallback((key) => {
    const seen = new Set();
    const options = [];
    accessFilteredRows.forEach((row) => {
      const raw = String(row?.[key] ?? "").trim();
      if (!raw) return;
      const token = raw.toLowerCase();
      if (seen.has(token)) return;
      seen.add(token);
      options.push({ value: raw, label: raw });
    });
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [accessFilteredRows]);
  const ibCodeOptions = useMemo(() => {
    const base = buildValueOptions(ILS_IB_COLUMN_KEY);
    return [
      { value: IB_FILTER_READY, label: "Ready" },
      { value: IB_FILTER_PENDING, label: "Pending" },
      ...base,
    ];
  }, [buildValueOptions]);
  const custCodeOptions = useMemo(() => {
    const base = buildValueOptions("cust_code");
    const hasBlank = accessFilteredRows.some((row) => !String(row?.cust_code || "").trim());
    return hasBlank ? [{ value: BLANK_CUST_OPTION, label: "(Blank)" }, ...base] : base;
  }, [buildValueOptions, accessFilteredRows]);
  const podOptions = useMemo(() => buildValueOptions("pod"), [buildValueOptions]);
  const hasBlankWarehouseRows = useMemo(
    () => rows.some((row) => !String(row?.warehouse || "").trim()),
    [rows],
  );
  const warehouseOptions = useMemo(() => {
    const seen = new Set();
    const mapped = whseCodes
      .map((code) => String(code || "").trim())
      .filter(Boolean)
      .filter((code) => {
        const key = code.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((code) => ({ value: code.toLowerCase(), label: code }));
    if (hasBlankWarehouseRows) {
      mapped.unshift({ value: BLANK_WAREHOUSE_OPTION, label: "(Blank)" });
    }
    return mapped;
  }, [whseCodes, hasBlankWarehouseRows]);

  const PAGE_OPTIONS = [50, 100, 200, "All"];
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  // 目前使用者
  const me   = getCurrentUser();
  const { getLevel, getLimitedConfig } = usePermissions();
  const myRole = String(me?.role || "").trim().toLowerCase();
  const myCode = String(me?.company_code || "").trim().toLowerCase();

  const permissionLevel = useMemo(() => getLevel(me?.role, "my-containers"), [getLevel, me?.role]);
  const limitedConfig = useMemo(
    () => (permissionLevel === "Limited" ? getLimitedConfig(me?.role, "myContainers") : null),
    [permissionLevel, getLimitedConfig, me?.role]
  );

  const defaultFeatureFlags = useMemo(
    () => FEATURE_FLAGS_BY_ROLE[myRole] || FEATURE_FLAGS_BY_ROLE.default,
    [myRole]
  );

  const featureFlags = useMemo(() => {
    if (permissionLevel === "Full") {
      return FEATURE_FLAGS_BY_ROLE.admin;
    }
    if (permissionLevel === "Limited") {
      const overrides = limitedConfig?.features || {};
      return {
        addRow: !!(overrides.addRow ?? defaultFeatureFlags.addRow),
        importData: !!(overrides.importData ?? defaultFeatureFlags.importData),
        exportData: !!(overrides.exportData ?? defaultFeatureFlags.exportData),
        deleteRows: !!(overrides.deleteRows ?? defaultFeatureFlags.deleteRows),
        saveChanges: !!(overrides.saveChanges ?? defaultFeatureFlags.saveChanges),
        getIbCode: !!(overrides.getIbCode ?? defaultFeatureFlags.getIbCode),
      };
    }
    if (permissionLevel === "Read" || permissionLevel === "None") {
      return FEATURE_FLAGS_BY_ROLE.overview;
    }
    return defaultFeatureFlags;
  }, [permissionLevel, limitedConfig, defaultFeatureFlags]);

  const canAddRow = featureFlags.addRow;
  const canImport = featureFlags.importData;
  const canExport = featureFlags.exportData;
  const canDelete = featureFlags.deleteRows;
  const canSave = featureFlags.saveChanges;
  const canGetIbCode = featureFlags.getIbCode;

  const defaultScope = useMemo(() => {
    if (myRole === "drayage") return { type: "company", field: "drayage" };
    if (myRole === "warehouse") return { type: "company", field: "warehouse" };
    return { type: "all", field: null };
  }, [myRole]);

  const scope = useMemo(() => {
    if (permissionLevel === "Limited" && limitedConfig?.scope) {
      const { type = "all", field = null } = limitedConfig.scope;
      return { type, field };
    }
    if (permissionLevel === "Full") {
      return { type: "all", field: null };
    }
    if (permissionLevel === "Read" || permissionLevel === "None") {
      return defaultScope;
    }
    return defaultScope;
  }, [permissionLevel, limitedConfig, defaultScope]);

  const permFor = useCallback(
    (key) => {
      if (permissionLevel === "Full") {
        return key === FIELD_KEYS.status ? "auto" : "edit";
      }
      if (permissionLevel === "Limited") {
        const fromConfig = limitedConfig?.fieldAccess?.[key];
        if (fromConfig) return fromConfig;
      }
      const roleKey = PERMS[myRole] ? myRole : "overview";
      return (PERMS[roleKey] || PERMS.overview)(key);
    },
    [permissionLevel, limitedConfig, myRole]
  );

  const canEdit = useCallback((key) => canSave && permFor(key) === "edit", [canSave, permFor]);

  const fileInputRef = useRef(null);
  const selectAllRef = useRef(null);

  // 儲存欄寬
  useEffect(() => {
    localStorage.setItem(WIDTHS_KEY, JSON.stringify(widths));
  }, [widths]);

  // 載入資料
  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await fetch(`${API_BASE}/my-containers`);
        const j = await r.json();
        if (!r.ok || !j.ok) throw new Error(j.error || "load failed");
        const normalized = normalizeRowsForState(j.rows || []);
        setRows(normalized);
        setSavedSnap(rowsSnapshot(normalized)); // 設定「已儲存快照」
        setHasUnsaved(false);
        mirrorDirtyToStorage(false);            // 初始化清乾淨
      } catch (e) {
        console.error(e); setErr("Load failed.");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!drillParams) return;
    setShowWarehouseNA(drillParams.whseCode === "ALL" && !drillParams.whseCodes?.length);
  }, [drillParams]);

  useEffect(() => {
    if (!drillParams) return undefined;
    let cancelled = false;

    (async () => {
      setBreakdownLoading(true);
      setBreakdownError("");
      try {
        const url = new URL(`${API_BASE}/api/mrp/operational-planner/intransit-breakdown`);
        url.searchParams.set("date_field", drillParams.dateField);
        url.searchParams.set("date_from", drillParams.dateFrom);
        url.searchParams.set("date_to", drillParams.dateTo);
        url.searchParams.set("whse_code", drillParams.whseCode || "ALL");
        if (Array.isArray(drillParams.whseCodes) && drillParams.whseCodes.length > 0) {
          url.searchParams.set("whse_codes", drillParams.whseCodes.join(","));
        }
        url.searchParams.set("show_warehouse_na", showWarehouseNA ? "1" : "0");
        if (drillParams.walmartItemNo) {
          url.searchParams.set("walmart_item_no", drillParams.walmartItemNo);
        }

        const response = await fetch(url.toString());
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "intransit_breakdown_failed");
        }
        if (cancelled) return;

        setBreakdownSummaryRows(Array.isArray(payload?.summary) ? payload.summary : []);
        setBreakdownDetailRows(Array.isArray(payload?.details) ? payload.details : []);
        setBreakdownTotal(toFiniteNumber(payload?.breakdown_total));
      } catch (fetchError) {
        if (cancelled) return;
        setBreakdownSummaryRows([]);
        setBreakdownDetailRows([]);
        setBreakdownTotal(0);
        setBreakdownError(fetchError?.message || "Load breakdown failed.");
      } finally {
        if (!cancelled) {
          setBreakdownLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [drillParams, showWarehouseNA]);

  useEffect(() => {
    setIbStatusMap((prev) => {
      const next = {};
      rows.forEach((row) => {
        const rowId = row?._id;
        if (!rowId) return;
        const cust = upperTrim(row?.cust_code);
        const container = upperTrim(row?.container);
        const storedCode = String(row?.[ILS_IB_COLUMN_KEY] ?? "").trim();
        if (!cust || !container) {
          const reason = !cust && !container
            ? "Cust Code & Container No required"
            : !cust
              ? "Cust Code required"
              : "Container No required";
          next[rowId] = makeIbEntry({ reason });
          return;
        }
        const hash = `${cust}|${container}`;
        if (storedCode) {
          next[rowId] = makeIbEntry({
            state: IB_STATES.FOUND,
            hash,
            code: storedCode,
            reason: "Stored",
          });
          return;
        }
        const prevEntry = prev[rowId];
        if (prevEntry && prevEntry.hash === hash) {
          next[rowId] = prevEntry;
        } else {
          next[rowId] = makeIbEntry({ state: IB_STATES.READY, hash, reason: "Ready to fetch" });
        }
      });
      return next;
    });
  }, [rows]);

  // 載入 Drayage / Warehouse Codes
  useEffect(() => {
    (async () => {
      try {
        const r1 = await fetch(`${API_BASE}/drayage`);
        const j1 = await r1.json().catch(() => ({}));
        setDrayageCodes((j1.rows || []).map(x => (x.drayage_code ?? x.code ?? "").trim()).filter(Boolean));

        const r2 = await fetch(`${API_BASE}/warehouse`);
        const j2 = await r2.json().catch(() => ({}));
        const warehouseCodeList = [];
        (j2.rows || []).forEach((x) => {
          const primary = String(x?.whse_code ?? x?.code ?? "").trim();
          const alias = String(x?.wms_code ?? "").trim();
          if (primary) warehouseCodeList.push(primary);
          if (alias && alias.toLowerCase() !== primary.toLowerCase()) {
            warehouseCodeList.push(alias);
          }
        });
        const deduped = [];
        const seen = new Set();
        warehouseCodeList.forEach((code) => {
          if (!code) return;
          const key = code.toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          deduped.push(code);
        });
        setWhseCodes(deduped);
      } catch (e) {
        console.error("load codes failed", e);
      }
    })();
  }, []);

  // 根據 rows 與 savedSnap 判定是否有未儲存變更（並同步到 localStorage）
  useEffect(() => {
    const dirty = rowsSnapshot(rows) !== savedSnap;
    setHasUnsaved(dirty);
    mirrorDirtyToStorage(dirty);
  }, [rows, savedSnap]);

  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const history = window.history;
    const confirmLeave = () => window.confirm("You have unsaved changes. Leave without saving?");
    const nativePush = history.pushState;
    const nativeReplace = history.replaceState;
    let suppressPopHandler = false;

    const wrapMethod = (nativeMethod) => {
      return function wrappedPushState(...args) {
        if (!confirmLeave()) {
          return undefined;
        }
        return nativeMethod.apply(history, args);
      };
    };

    history.pushState = wrapMethod(nativePush);
    history.replaceState = wrapMethod(nativeReplace);

    const handlePopState = () => {
      if (suppressPopHandler) {
        suppressPopHandler = false;
        return;
      }
      if (!confirmLeave()) {
        suppressPopHandler = true;
        history.go(1);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      history.pushState = nativePush;
      history.replaceState = nativeReplace;
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsaved]);


  // 關閉分頁/重新整理提示
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!hasUnsaved) return;
      e.preventDefault();
      e.returnValue = ""; // Chrome requires this to show the prompt
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsaved]);

  /** 新增一列 */
  const onAddRow = () => {
    setRows(r => [{ _id: genId(), status: "Planning", m_closed: false }, ...r]);
  };

  /** 勾選／刪除 */
  const toggleSelect = (rowId, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(rowId); else next.delete(rowId);
      return next;
    });
  };
  const askDelete = () => {
    const n = selectedIds.size; if (!n) return;
    setToDeleteCount(n); setDeleteOpen(true);
  };
  const confirmDelete = () => {
    setRows(prev => prev.filter(r => !selectedIds.has(r._id)));
    setSelectedIds(new Set()); setDeleteOpen(false);
  };

  const patchIbEntry = useCallback((rowId, updater) => {
    if (!rowId) return;
    setIbStatusMap((prev) => {
      const current = prev[rowId] || makeIbEntry();
      const nextEntry = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [rowId]: nextEntry };
    });
  }, []);

  const copyInboundCode = useCallback((value) => {
    const text = String(value || "").trim();
    if (!text) return;
    const fallback = () => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch {}
    };
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(fallback);
  } else {
    fallback();
  }
}, []);

const persistIlsInboundCode = useCallback(
  async (orderNo, inboundCode) => {
    if (!orderNo) {
      throw new Error("order_no_missing");
    }
    const headers = { "Content-Type": "application/json" };
    if (me?.email) headers["X-User-Email"] = me.email;
    if (me?.role) headers["X-User-Role"] = me.role || "";
    const response = await fetch(`${API_BASE}/my-containers/ils-ib-code`, {
      method: "POST",
      headers,
      body: JSON.stringify({ order_no: orderNo, ils_ib_code: inboundCode }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.ok === false) {
      throw new Error(data?.error || "save_failed");
    }
    return data;
  },
  [me?.email, me?.role]
);

  const fetchIbForRow = useCallback(
    async (row) => {
      const rowId = row?._id;
      if (!rowId) return;
      const orderNo = String(row?.order_no || "").trim();
      const previousCode = String(row?.[ILS_IB_COLUMN_KEY] || "").trim();
      const cust = upperTrim(row?.cust_code);
      const container = upperTrim(row?.container);
      if (!cust || !container) {
        patchIbEntry(rowId, () =>
          makeIbEntry({
            reason: !cust && !container
              ? "Cust Code & Container No required"
              : !cust
                ? "Cust Code required"
                : "Container No required",
          })
        );
        return;
      }
        const hash = `${cust}|${container}`;
        patchIbEntry(rowId, () =>
          makeIbEntry({
            state: IB_STATES.FETCHING,
            hash,
            reason: "Fetching from iLS",
          })
        );
        const headers = { "Content-Type": "application/json" };
        if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      try {
        const response = await fetch(`${API_BASE}/integrations/ils/inbound-code`, {
          method: "POST",
          headers,
          body: JSON.stringify({ cust_code: cust, container_no: container }),
        });
        const data = await response.json().catch(() => ({}));
        if (response.status === 404 || data?.error === "not_found") {
          patchIbEntry(rowId, () =>
            makeIbEntry({
              state: IB_STATES.NOT_FOUND,
              hash,
              reason: "Container not found in iLS",
            })
          );
          return;
        }
        if (!response.ok || data?.ok === false) {
          const message = data?.message || data?.error || `HTTP ${response.status}`;
          patchIbEntry(rowId, () =>
            makeIbEntry({
              state: IB_STATES.ERROR,
              hash,
              error: message,
              reason: message,
            })
          );
          return;
        }
        const inboundCode = String(data?.inboundCode || "").trim();
        if (!inboundCode) {
          patchIbEntry(rowId, () =>
            makeIbEntry({
              state: IB_STATES.ERROR,
              hash,
              error: "Missing inboundCode",
              reason: "Missing inboundCode",
            })
          );
          return;
        }
        const needsPersist = inboundCode !== previousCode;
        patchIbEntry(rowId, () =>
          makeIbEntry({
            state: IB_STATES.FOUND,
            hash,
            code: inboundCode,
            reason: needsPersist ? "Saving..." : "Stored",
          })
        );
        let nextRowsState = null;
        if (needsPersist) {
          setRows((prev) => {
            const next = prev.map((item) =>
              item._id === rowId ? { ...item, [ILS_IB_COLUMN_KEY]: inboundCode } : item
            );
            nextRowsState = next;
            return next;
          });
          if (!orderNo) {
            setRows((prev) =>
              prev.map((item) =>
                item._id === rowId ? { ...item, [ILS_IB_COLUMN_KEY]: previousCode } : item
              )
            );
            patchIbEntry(rowId, () =>
              makeIbEntry({
                state: IB_STATES.ERROR,
                hash,
                error: "Missing Order No for save",
                reason: "Save failed",
              })
            );
            return;
          }
          const hadManualDirty = hasUnsavedRef.current;
          try {
            await persistIlsInboundCode(orderNo, inboundCode);
            if (!hadManualDirty && nextRowsState) {
              const snap = rowsSnapshot(nextRowsState);
              setSavedSnap(snap);
              setHasUnsaved(false);
              mirrorDirtyToStorage(false);
            }
            patchIbEntry(rowId, () =>
              makeIbEntry({
                state: IB_STATES.FOUND,
                hash,
                code: inboundCode,
                reason: "Stored",
              })
            );
          } catch (saveErr) {
            setRows((prev) =>
              prev.map((item) =>
                item._id === rowId ? { ...item, [ILS_IB_COLUMN_KEY]: previousCode } : item
              )
            );
            patchIbEntry(rowId, () =>
              makeIbEntry({
                state: IB_STATES.ERROR,
                hash,
                error: saveErr?.message || "Save failed",
                reason: "Save failed",
              })
            );
            return;
          }
        } else {
          patchIbEntry(rowId, () =>
            makeIbEntry({
              state: IB_STATES.FOUND,
              hash,
              code: inboundCode,
              reason: "Stored",
            })
          );
        }
      } catch (ex) {
        const message = ex?.message || "Request failed";
        patchIbEntry(rowId, () =>
          makeIbEntry({
            state: IB_STATES.ERROR,
            hash,
            error: message,
            reason: message,
          })
        );
      }
    },
    [patchIbEntry, me, persistIlsInboundCode]
  );

  const onGetIbCode = useCallback(async () => {
    if (!canGetIbCode || ibBatchState.running) return;
    const queue = rows.filter((row) => {
      const id = row?._id;
      if (!id) return false;
      if (!selectedIds.has(id)) return false;
      const entry = ibStatusMap[id];
      return entry && IB_FETCHABLE_STATES.has(entry.state);
    });
    if (!queue.length) return;
    setIbBatchState({ running: true, total: queue.length, processed: 0 });
    try {
      for (let i = 0; i < queue.length; i += 1) {
        await fetchIbForRow(queue[i]);
        setIbBatchState({ running: true, total: queue.length, processed: i + 1 });
        if (i < queue.length - 1) await delay(ILS_FETCH_DELAY_MS);
      }
    } finally {
      setIbBatchState({ running: false, total: 0, processed: 0 });
    }
  }, [canGetIbCode, ibBatchState.running, rows, selectedIds, ibStatusMap, fetchIbForRow]);

  /** 單格編輯／失焦處理 */
  const onCellChange = (rowId, key, v) => {
    if (!canEdit(key)) return;
    setRows(prev => {
      const i = prev.findIndex(r => r._id === rowId);
      if (i < 0) return prev;
      const next = [...prev];
      next[i] = { ...next[i], [key]: v };
      return next;
    });
  };

  const onCellBlur = (rowId, key) => {
    if (!canEdit(key)) return;
    setRows(prev => {
      const i = prev.findIndex(r => r._id === rowId);
      if (i < 0) return prev;

      const list = [...prev];
      const row  = { ...list[i] };
      const val  = row[key];

      // normalize dates/datetimes
      if (DATE_FIELDS.has(key))            row[key] = normalizeDateInput(val, false) || "";
      else if (DATETIME_FIELDS.has(key))   row[key] = normalizeDateInput(val, true)  || "";

      if (key === "m_closed") {
        row[key] = toBool(val);
      }

      // Order No duplicate check
      if (key === "order_no") {
        const k = normalizeOrderNo(row[key]);
        if (k) {
          const dup = list.findIndex((r, idx) => idx !== i && normalizeOrderNo(r.order_no) === k);
          if (dup !== -1) {
            alert(`Order No. "${row[key]}" already exists at row ${dup + 1}. It has been cleared.`);
            row[key] = "";
          }
        }
      }

      // 影響狀態的欄位變更 → 重算
      if (AFFECTS_STATUS.has(key)) row.status = computeStatus(row);

      list[i] = row;
      return list;
    });
  };

  /** 儲存 */
  const onSave = async () => {
    const dupList = findDuplicateOrderNos(rows);
    if (dupList.length) {
      setErr(`Duplicate Order No.: ${dupList.join(", ")}. Please resolve before saving.`);
      return;
    }
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${API_BASE}/my-containers`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "save failed");

      // 儲存成功 → 更新「已儲存快照」並清掉 dirty（含 localStorage）
      const snap = rowsSnapshot(rows);
      setSavedSnap(snap);
      setHasUnsaved(false);
      mirrorDirtyToStorage(false);
    } catch (e) {
      console.error(e); setErr("Save failed.");
    } finally { setLoading(false); }
  };

  /** 匯出 */
  const onExport = () => {
    const exportRows = sorted;
    if (!exportRows.length) return;
    const aoa = [
      COLUMNS.map(c => c.label),
      ...exportRows.map(r => COLUMNS.map(c => formatForDisplay(r[c.key], c))),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, "MyContainers");
    XLSX.writeFile(wb, "MyContainers.xlsx");
  };

  /** 匯入 */
  const onClickImport = () => {
    if (!canImport) return;
    setImportConfirmOpen(true);
  };
  const onImportFile = async (e) => {
    setErr("");
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf, { type: "array" });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error("No worksheet found.");
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
      if (aoa.length < 2) throw new Error("Worksheet has no data.");

      const headers  = (aoa[0] || []).map(h => String(h || "").trim());
      const expected = COLUMNS.map(c => c.label);
      const missing  = expected.filter(l => !headers.includes(l));
      if (missing.length) {
        throw new Error(`Invalid columns.\nMissing: ${missing.join(", ")}\nHeaders in file: ${headers.join(", ")}`);
      }

      const parsedRows = aoa.slice(1).map((rowArr) => {
        const obj = {};
        COLUMNS.forEach((c) => {
          const idx  = headers.indexOf(c.label);
          const cell = idx >= 0 ? rowArr[idx] : "";
          obj[c.key] = normalizeCellForImport(cell, c);
        });
        obj.m_closed = toBool(obj.m_closed);
        obj.status = computeStatus(obj);
        return obj;
      });

      const existingOrderSet = new Set(
        rows.map((r) => normalizeOrderNo(r.order_no || r.orderNo || "")),
      );
      const allowedRows = [];
      let skippedCount = 0;
      parsedRows.forEach((row) => {
        const raw = String(row.order_no || "").trim();
        if (!raw) {
          skippedCount += 1;
          return;
        }
        const key = normalizeOrderNo(raw);
        if (!existingOrderSet.has(key)) {
          skippedCount += 1;
          return;
        }
        allowedRows.push(row);
      });

      if (!allowedRows.length) {
        alert("No matching Order No. found in the current dataset. Nothing imported.");
        return;
      }

      const existingByOrder = new Map(rows.map((r, i) => [normalizeOrderNo(r.order_no), { idx: i, row: r }]));
      let cancelled = false;
      const merged = [...rows];

      for (const newRow of allowedRows) {
        const raw = String(newRow.order_no || "").trim();
        if (!raw) { continue; }
        const key = normalizeOrderNo(raw);

        if (!existingByOrder.has(key)) {
          continue;
        }

        const action = window.prompt(
          `Duplicate Order No. "${raw}".\nType "o" to Overwrite, "s" to Skip, "c" to Cancel import.`,
          "o"
        );
        if (!action) continue;
        const a = action.toLowerCase();
        if (a === "c") { cancelled = true; break; }
        if (a === "s") { continue; }
        if (a === "o") {
          const { idx } = existingByOrder.get(key);
          merged[idx] = { ...merged[idx], ...newRow };
        }
      }

      if (cancelled) { alert("Import cancelled."); return; }
      setRows(normalizeRowsForState(merged));
      const finalMsg =
        skippedCount > 0
          ? `Import complete. ${merged.length} rows total (skipped ${skippedCount} unmatched). Click "Save" to apply.`
          : `Import complete. New total: ${merged.length}. Click "Save" to apply.`;
      alert(finalMsg);
    } catch (ex) {
      console.error(ex); setErr(ex.message || "Import failed.");
    }
  };

  const inTransitDrillActive = Boolean(drillParams);
  const inTransitOrderScope = useMemo(() => {
    if (!inTransitDrillActive) return null;
    const scope = new Set();
    breakdownDetailRows.forEach((row) => {
      const key = normalizeOrderNo(row?.order_no);
      if (key) scope.add(key);
    });
    return scope;
  }, [inTransitDrillActive, breakdownDetailRows]);

  /** 角色/公司代碼過濾 + 逐欄位過濾 + 排序（同一 useMemo） */
  const sorted = useMemo(() => {
    const eq = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
    const like = (val, q) => String(val || "").toLowerCase().includes(String(q || "").toLowerCase());
    const inRange = (val, f, t) => {
      if (!f && !t) return true;
      if (!val) return false;
      const ts  = toDayTS(val);
      if (!Number.isFinite(ts)) return false;
      const fs  = toDayTS(f);
      const ts2 = toDayTS(t);
      if (Number.isFinite(fs) && ts < fs)  return false;
      if (Number.isFinite(ts2) && ts > ts2) return false;
      return true;
    };

    // 1) 角色/公司代碼
    let src = accessFilteredRows;
    if (scope.type === "company" && scope.field && myCode) {
      src = src.filter((r) => eq(r?.[scope.field], myCode));
    }
    if (inTransitDrillActive && inTransitOrderScope instanceof Set) {
      src = src.filter((r) => inTransitOrderScope.has(normalizeOrderNo(r?.order_no)));
    }

    // 2) 逐欄位 filter
    const f = filters;
    src = src.filter((r) => {
      const statusFilters = toArray(f.status);
      const matchesStatus =
        statusFilters.length === 0 ||
        statusFilters.some(
          (statusValue) =>
            eq(r.status, statusValue) ||
            (statusValue === "Closed" && eq(r.status, "Returned"))
        );

      const drayageFilters = toArray(f.drayage);
      const matchesDrayage =
        drayageFilters.length === 0 ||
        drayageFilters.some((code) => {
          if (code === BLANK_DRAYAGE_OPTION) {
            return !String(r.drayage || "").trim();
          }
          return eq(r.drayage, code);
        });

      const warehouseFilters = toArray(f.warehouse);
      const matchesWarehouse =
        warehouseFilters.length === 0 ||
        warehouseFilters.some((code) => {
          if (code === BLANK_WAREHOUSE_OPTION) {
            return !String(r.warehouse || "").trim();
          }
          return eq(r.warehouse, code);
        });

      const ibFilters = toArray(f.ils_ib_code);
      const ibEntry = ibStatusMap[r?._id];
      const ibState = ibEntry?.state || IB_STATES.PENDING;
      const matchesIbCode =
        ibFilters.length === 0 ||
        ibFilters.some((code) => {
          if (code === IB_FILTER_READY) return ibState === IB_STATES.READY;
          if (code === IB_FILTER_PENDING) return ibState === IB_STATES.PENDING;
          return eq(r[ILS_IB_COLUMN_KEY], code);
        });

      const custFilters = toArray(f.cust_code);
      const matchesCustCode =
        custFilters.length === 0 ||
        custFilters.some((code) => {
          if (code === BLANK_CUST_OPTION) {
            return !String(r.cust_code || "").trim();
          }
          return eq(r.cust_code, code);
        });

      const podFilters = toArray(f.pod);
      const matchesPod =
        podFilters.length === 0 || podFilters.some((code) => eq(r.pod, code));

      let matchesManualClosed = true;
      if (f.m_closed === "true") matchesManualClosed = toBool(r.m_closed);
      else if (f.m_closed === "false") matchesManualClosed = !toBool(r.m_closed);

      return (
        like(r.order_no, f.order_no) &&
        like(r.mbl_no,   f.mbl_no)   &&
        like(r.container,f.container) &&
        matchesPod &&
        matchesIbCode &&
        matchesCustCode &&
        matchesStatus &&
        matchesManualClosed &&
        matchesDrayage &&
        matchesWarehouse &&
        inRange(r.etd,           f.etdFrom,           f.etdTo) &&
        inRange(r.eta,           f.etaFrom,           f.etaTo) &&
        inRange(r.arrived,       f.arrivedFrom,       f.arrivedTo) &&
        inRange(r.lfd,           f.lfdFrom,           f.lfdTo) &&
        inRange(r.appt_date,     f.appt_dateFrom,     f.appt_dateTo) &&
        inRange(r.lrd,           f.lrdFrom,           f.lrdTo) &&
        inRange(r.delivered_dt,  f.delivered_dtFrom,  f.delivered_dtTo) &&
        inRange(r.emptied_dt,    f.emptied_dtFrom,    f.emptied_dtTo) &&
        inRange(r.returned_date, f.returned_dateFrom, f.returned_dateTo)
      );
    });

    // 3) 排序
    if (sortDir === 0 || !sortKey) return src;
    const isDate     = DATE_FIELDS.has(sortKey);
    const isDateTime = DATETIME_FIELDS.has(sortKey);
    const copy = [...src];
    copy.sort((a, b) => {
      const va = a?.[sortKey] ?? "";
      const vb = b?.[sortKey] ?? "";
      if (isDate || isDateTime) {
        const da = va ? new Date(va).getTime() : 0;
        const db = vb ? new Date(vb).getTime() : 0;
        return sortDir * (da - db);
      }
      return sortDir * String(va).localeCompare(String(vb));
    });
    return copy;
  }, [accessFilteredRows, scope, myCode, filters, sortKey, sortDir, ibStatusMap, inTransitDrillActive, inTransitOrderScope]);

  // 分頁
  const totalPages = useMemo(() => {
    const size = pageSize === "All" ? (sorted.length || 1) : pageSize;
    return Math.max(1, Math.ceil(sorted.length / size));
  }, [sorted, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    if (pageSize === "All") return sorted;
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const visibleRowIds = useMemo(
    () => paged.map((row) => row?._id).filter((id) => !!id),
    [paged]
  );
  const allVisibleSelected = visibleRowIds.length > 0 && visibleRowIds.every((id) => selectedIds.has(id));
  const hasVisibleSelected = visibleRowIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = hasVisibleSelected && !allVisibleSelected;
    }
  }, [hasVisibleSelected, allVisibleSelected]);

  const toggleSelectAllVisible = useCallback(
    (checked) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visibleRowIds.forEach((id) => {
          if (!id) return;
          if (checked) next.add(id);
          else next.delete(id);
        });
        return next;
      });
    },
    [visibleRowIds]
  );

  const fetchableSelectedIds = useMemo(() => {
    if (!canGetIbCode) return [];
    return Array.from(selectedIds).filter((id) => {
      const entry = ibStatusMap[id];
      return entry && IB_FETCHABLE_STATES.has(entry.state);
    });
  }, [selectedIds, ibStatusMap, canGetIbCode]);
  const fetchableCount = fetchableSelectedIds.length;
  const isIbFetching = ibBatchState.running;

  const startIdx = pageSize === "All" ? 0 : (page - 1) * pageSize;
  const endIdx   = pageSize === "All" ? sorted.length : Math.min(sorted.length, startIdx + pageSize);

  /** 欄寬拖拉 */
  const dragRef = useRef({ col: "", startX: 0, startW: 0 });
  const onDragStart = (e, colKey) => {
    dragRef.current = { col: colKey, startX: e.clientX, startW: widths[colKey] || 120 };
    window.addEventListener("mousemove", onDragging);
    window.addEventListener("mouseup", onDragEnd);
    e.preventDefault();
  };
  const onDragging = (e) => {
    const { col, startX, startW } = dragRef.current;
    if (!col) return;
    const dx = e.clientX - startX;
    const newW = Math.max(60, startW + dx);
    setWidths(w => ({ ...w, [col]: newW }));
  };
  const onDragEnd = () => {
    dragRef.current = { col: "", startX: 0, startW: 0 };
    window.removeEventListener("mousemove", onDragging);
    window.removeEventListener("mouseup", onDragEnd);
  };

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir(1); }
    else setSortDir(d => (d === 1 ? -1 : d === -1 ? 0 : 1));
  };

  // 共用 DateRangeFilter（以 value={{from,to}} 傳入）
  const renderDateRangeFilter = (key) => (
    <DateRangeFilter
      value={{
        from: filters[`${key}From`] || "",
        to:   filters[`${key}To`]   || "",
      }}
      onChange={({ from, to }) => {
        setFilter(`${key}From`, from || "");
        setFilter(`${key}To`,   to   || "");
      }}
      placeholder="From-To"
    />
  );

  const drillTitle =
    drillParams?.drill === INTRANSIT_DRILL_ARRIVAL ? "In Transit - Arrival" : "In Transit - Depart";
  const drillDateFieldLabel =
    drillParams?.dateField === "arrival_effective_date" ? "Arrival Effective Date" : "ETD";
  const drillWhseLabel =
    Array.isArray(drillParams?.whseCodes) && drillParams.whseCodes.length
      ? drillParams.whseCodes.join(", ")
      : drillParams?.whseCode || "ALL";
  const breakdownMismatch =
    Boolean(drillParams?.hasCellValue) &&
    Math.abs(toFiniteNumber(breakdownTotal) - toFiniteNumber(drillParams?.cellValue)) > 0.0001;

  return (
    <div className="p-4">
      <div className="mb-2 flex items-center gap-2">
        <h1 className="text-xl font-bold">My Containers</h1>
        <CurrentUserBadge />
        <UserAccessSummary className="ml-4" />


        {canAddRow && (
          <button className="px-3 py-1 rounded bg-gray-100 border" onClick={onAddRow}>+ Add Row</button>
        )}

        <button
          className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onSave}
          disabled={loading || !canSave}
          title={!canSave ? "Your role cannot save changes" : ""}
        >
          {loading ? "Saving..." : "Save"}
        </button>
        <button
          className="px-3 py-1 rounded bg-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onExport}
          disabled={!canExport}
          title={!canExport ? "Your role cannot export" : ""}
        >
          Export Excel
        </button>
        {canGetIbCode && (
          <button
            className="px-3 py-1 rounded bg-sky-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onGetIbCode}
            disabled={!fetchableCount || isIbFetching}
            title={!fetchableCount ? "Select Ready rows with Cust Code + Container" : ""}
          >
            {isIbFetching
              ? `Fetching ${ibBatchState.processed}/${ibBatchState.total || fetchableCount}`
              : fetchableCount
                ? `Get IB Code (${fetchableCount})`
                : "Get IB Code"}
          </button>
        )}

        {(canImport || canDelete) && (
          <>
            {canImport && (
              <button
                className="px-3 py-1 rounded bg-amber-600 text-white"
                onClick={onClickImport}
              >
                Import Excel
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={askDelete}
                disabled={!canDelete || selectedIds.size === 0}
                className={!canDelete || selectedIds.size === 0
                  ? "px-3 py-1 rounded bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"}
                title={!canDelete
                  ? "Your role cannot delete rows"
                  : selectedIds.size
                    ? `Delete ${selectedIds.size} selected row(s)`
                    : "Select rows to enable"}
              >
                Delete
              </button>
            )}
          </>
        )}

        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onImportFile} />

        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-gray-600">Rows:</span>
          <select
            className="border rounded px-2 py-1 text-sm"
            value={pageSize}
            onChange={(e) => {
              const v = e.target.value;
              setPageSize(v === "All" ? "All" : parseInt(v, 10));
              setPage(1);
            }}
          >
            {PAGE_OPTIONS.map(opt => (<option key={opt} value={opt}>{opt}</option>))}
          </select>

          <button
            type="button"
            className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => setPage(1)}
            disabled={page <= 1}
            aria-label="First page"
          >
            {"<<"}
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            {"<"}
          </button>
          <span className="text-sm tabular-nums">{page} / {totalPages}</span>
          <button
            type="button"
            className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            aria-label="Next page"
          >
            {">"}
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => setPage(totalPages)}
            disabled={page >= totalPages}
            aria-label="Last page"
          >
            {">>"}
          </button>
          <span className="ml-2 text-sm text-gray-600 tabular-nums">
            {sorted.length ? `${startIdx + 1} - ${endIdx} of ${sorted.length}` : "0"}
          </span>
        </div>

        {err && <span className="text-red-600 ml-2">{err}</span>}
      </div>

      <div className="relative border rounded max-h-[calc(110vh-180px)] overflow-auto min-h-[20rem]">
        <table className="min-w-full border-collapse">
          <thead>
            {/* 標題列 */}
            <tr>
              <th style={{ width: 36, minWidth: 36 }} className="sticky top-0 bg-white border-b px-2 py-2">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="h-4 w-4"
                  checked={visibleRowIds.length > 0 && allVisibleSelected}
                  onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                  disabled={visibleRowIds.length === 0}
                  aria-label="Select all visible rows"
                />
              </th>
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ width: widths[col.key], minWidth: widths[col.key] }}
                  className="sticky top-0 bg-white border-b px-2 py-2 text-left text-sm font-semibold relative select-none"
                >
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleSort(col.key)} className="text-left">
                      {col.label}
                      {sortKey === col.key ? (sortDir === 1 ? " ▲" : sortDir === -1 ? " ▼" : "") : ""}
                    </button>
                    <span
                      onMouseDown={(e) => onDragStart(e, col.key)}
                      className="absolute right-0 top-0 h-full w-1 cursor-col-resize"
                      style={{ userSelect: "none" }}
                      title="Drag to resize"
                    />
                  </div>
                </th>
              ))}
            </tr>

            {/* 過濾列（<tr> 內嚴禁多餘的文字節點） */}
            <tr className="sticky top-[42px] bg-white">
              {/* 左側 checkbox 欄：放 Reset */}
              <th style={{ width: 36, minWidth: 36 }} className="bg-white border-b px-2 py-1">
                <button
                  type="button"
                  onClick={resetFilters}
                  title="Reset filters"
                  aria-label="Reset filters"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full
                            bg-green-600 text-white hover:bg-green-700
                            focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                >
                  {/* clockwise refresh arrow */}
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-3-6.7L21 7" />
                    <path d="M21 3v4h-4" />
                  </svg>
                </button>
              </th>

              {/* 每欄各自的過濾 UI */}
              {TABLE_COLUMNS.map((col) => {
                const wStyle = { width: widths[col.key], minWidth: widths[col.key] };
                const cellCls = "bg-emerald-50 border-b px-2 py-1";

                if (col.key === ILS_IB_COLUMN_KEY) {
                  return (
                    <th key={col.key} style={wStyle} className={cellCls}>
                      <MultiSelectDropdown
                        value={filters.ils_ib_code}
                        options={ibCodeOptions}
                        onChange={(next) => setFilter("ils_ib_code", next)}
                        placeholder="All"
                        buttonClassName="text-xs"
                        showSelectAll
                        showSearch
                      />
                    </th>
                  );
                }

                // 1) Status 下拉
                if (col.key === "status") {
                  return (
                    <th key={col.key} style={wStyle} className={cellCls}>
                      <MultiSelectDropdown
                        value={filters.status}
                        options={STATUS_FILTER_OPTIONS}
                        onChange={(next) => setFilter("status", next)}
                        placeholder="All"
                        buttonClassName="text-xs"
                        showSelectAll
                        showSearch
                      />
                    </th>
                  );
                }
                if (col.key === "m_closed") {
                  return (
                    <th key={col.key} style={wStyle} className={cellCls}>
                      <select
                        className="w-full border rounded px-2 py-1 text-xs"
                        value={filters.m_closed}
                        onChange={(e) => setFilter("m_closed", e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </th>
                  );
                }

                // 2) Drayage / Warehouse 下拉
                if (col.key === "drayage") {
                  return (
                    <th key={col.key} style={wStyle} className={cellCls}>
                      <MultiSelectDropdown
                        value={filters.drayage}
                        options={drayageOptions}
                        onChange={(next) => setFilter("drayage", next)}
                        placeholder="All"
                        buttonClassName="text-xs"
                        showSelectAll
                        showSearch
                      />
                    </th>
                  );
                }
                if (col.key === "warehouse") {
                  return (
                    <th key={col.key} style={wStyle} className={cellCls}>
                      <MultiSelectDropdown
                        value={filters.warehouse}
                        options={warehouseOptions}
                        onChange={(next) => setFilter("warehouse", next)}
                        placeholder="All"
                        buttonClassName="text-xs"
                        showSelectAll
                        showSearch
                      />
                    </th>
                  );
                }
                if (col.key === "cust_code") {
                  return (
                    <th key={col.key} style={wStyle} className={cellCls}>
                      <MultiSelectDropdown
                        value={filters.cust_code}
                        options={custCodeOptions}
                        onChange={(next) => setFilter("cust_code", next)}
                        placeholder="All"
                        buttonClassName="text-xs"
                        showSelectAll
                        showSearch
                      />
                    </th>
                  );
                }
                if (col.key === "pod") {
                  return (
                    <th key={col.key} style={wStyle} className={cellCls}>
                      <MultiSelectDropdown
                        value={filters.pod}
                        options={podOptions}
                        onChange={(next) => setFilter("pod", next)}
                        placeholder="All"
                        buttonClassName="text-xs"
                        showSelectAll
                        showSearch
                      />
                    </th>
                  );
                }

                // 3) 日期 / 日期時間 欄位：統一用 DateRangeFilter（單一元件）
                if (col.type === "date" || col.type === "datetime") {
                  return (
                    <th key={col.key} style={wStyle} className={cellCls}>
                      {renderDateRangeFilter(col.key)}
                    </th>
                  );
                }

                // 4) 其他文字欄位：Search
                return (
                  <th key={col.key} style={wStyle} className={cellCls}>
                    <input
                      className="w-full border rounded px-2 py-1 text-xs"
                      placeholder="Search"
                      value={filters[col.key] || ""}
                      onChange={(e) => setFilter(col.key, e.target.value)}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length + 1} className="text-center text-gray-500 py-8">No data.</td>
              </tr>
            ) : (
              paged.map((row, idx) => (
                <tr key={row._id ?? idx} className="border-t">
                  {/* 勾選欄 */}
                  <td style={{ width: 36, minWidth: 36 }} className="px-2 py-1 align-top">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row._id)}
                      onChange={(e) => toggleSelect(row._id, e.target.checked)}
                    />
                  </td>

                  {TABLE_COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      style={{ width: widths[col.key], minWidth: widths[col.key] }}
                      className="px-2 py-1 align-top"
                    >
                      {col.key === "status" ? (
                        <StatusBadge value={row.status} />
                      ) : col.key === ILS_IB_COLUMN_KEY ? (
                        <InboundCodeBadge entry={ibStatusMap[row._id]} onCopy={copyInboundCode} />
                      ) : col.key === "m_closed" ? (
                        <CheckboxCell
                          value={row.m_closed}
                          readOnly={!canEdit("m_closed")}
                          onChange={(checked) => {
                            onCellChange(row._id, "m_closed", checked);
                            onCellBlur(row._id, "m_closed");
                          }}
                        />
                      ) : col.key === "drayage" ? (
                        <SelectCell
                          value={row.drayage}
                          options={drayageCodes}
                          readOnly={!canEdit("drayage")}
                          placeholder="-- Select Drayage Code --"
                          onChange={(v) => onCellChange(row._id, "drayage", v)}
                          onBlur={() => onCellBlur(row._id, "drayage")}
                        />
                      ) : col.key === "warehouse" ? (
                        <SelectCell
                          value={row.warehouse}
                          options={whseCodes}
                          readOnly={!canEdit("warehouse")}
                          placeholder="-- Select WHSE Code --"
                          onChange={(v) => onCellChange(row._id, "warehouse", v)}
                          onBlur={() => onCellBlur(row._id, "warehouse")}
                        />
                      ) : (
                        <Cell
                          value={row[col.key]}
                          col={col}
                          readOnly={!canEdit(col.key)}
                          onChange={(v) => onCellChange(row._id, col.key, v)}
                          onBlur={() => onCellBlur(row._id, col.key)}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>

        <ConfirmModal
          open={importConfirmOpen}
          title="Import My Containers"
          message="Importing will update matching Order Nos only; it will not create new orders."
          confirmText="Continue"
          cancelText="Cancel"
          onConfirm={() => {
            setImportConfirmOpen(false);
            fileInputRef.current?.click();
          }}
          onCancel={() => setImportConfirmOpen(false)}
        />

        <ConfirmModal
          open={isDeleteOpen}
          title="Remove selected rows"
          message={`You’re about to remove ${toDeleteCount} selected ${toDeleteCount === 1 ? "row" : "rows"}.\n\nNote: they won’t be permanently deleted until you click “Save”.`}
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteOpen(false)}
        />
      </div>

      {inTransitDrillActive && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Container Breakdown (In Transit Source)</h2>
              <p className="text-xs text-slate-600">
                {drillTitle} | {drillDateFieldLabel}: {drillParams?.dateFrom} to {drillParams?.dateTo} | WHSE: {drillWhseLabel}
                {drillParams?.walmartItemNo ? ` | SKU: ${drillParams.walmartItemNo}` : ""}
              </p>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showWarehouseNA}
                onChange={(event) => setShowWarehouseNA(event.target.checked)}
                className="h-4 w-4 accent-blue-600"
              />
              Show Warehouse N/A
            </label>
          </div>

          {breakdownMismatch && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <div className="font-semibold">Drill-down totals do not match</div>
              <div>MRP cell value and breakdown total are different. Please verify date range semantics and filters.</div>
            </div>
          )}

          {breakdownError && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{breakdownError}</div>
          )}

          {breakdownLoading ? (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600">Loading breakdown...</div>
          ) : (
            <>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className={`rounded-md border px-3 py-1.5 text-sm ${breakdownTab === INTRANSIT_TAB_SUMMARY ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-700"}`}
                  onClick={() => setBreakdownTab(INTRANSIT_TAB_SUMMARY)}
                >
                  Summary by SKU
                </button>
                <button
                  type="button"
                  className={`rounded-md border px-3 py-1.5 text-sm ${breakdownTab === INTRANSIT_TAB_DETAILS ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-700"}`}
                  onClick={() => setBreakdownTab(INTRANSIT_TAB_DETAILS)}
                >
                  Details
                </button>
                <div className="ml-auto text-sm text-slate-700">
                  Breakdown Total: <span className="font-semibold">{formatQtyValue(breakdownTotal)}</span>
                </div>
              </div>

              {breakdownTab === INTRANSIT_TAB_SUMMARY ? (
                <div className="mt-3 overflow-auto rounded border border-slate-200">
                  <table className="min-w-[420px] w-full border-collapse text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">SKU</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right font-semibold text-slate-700">Total Pieces</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdownSummaryRows.length ? (
                        breakdownSummaryRows.map((row, index) => (
                          <tr key={`${row?.retail_sku || "sku"}-${index}`} className="border-b border-slate-100">
                            <td className="px-3 py-2 text-slate-800">{row?.retail_sku || "-"}</td>
                            <td className="px-3 py-2 text-right font-mono text-slate-800">{formatQtyValue(row?.pieces)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="px-3 py-6 text-center text-slate-500">
                            No breakdown rows.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-3 overflow-auto rounded border border-slate-200">
                  <table className="min-w-[1200px] w-full border-collapse text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">SKU</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Product Code</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Order No</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">MBL</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Container</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">WHSE</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">ETD</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">ETA</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Arrival Basis</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Arrival Date</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right font-semibold text-slate-700">Pieces</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdownDetailRows.length ? (
                        breakdownDetailRows.map((row, index) => (
                          <tr key={`${row?.order_no || "order"}-${row?.container || "container"}-${index}`} className="border-b border-slate-100">
                            <td className="px-3 py-2 text-slate-800">{row?.retail_sku || "-"}</td>
                            <td className="px-3 py-2 text-slate-800">{row?.product_code || "-"}</td>
                            <td className="px-3 py-2 text-slate-800">{row?.order_no || "-"}</td>
                            <td className="px-3 py-2 text-slate-800">{row?.mbl_no || "-"}</td>
                            <td className="px-3 py-2 text-slate-800">{row?.container || "-"}</td>
                            <td className="px-3 py-2 text-slate-800">{String(row?.warehouse || "").trim() || "N/A"}</td>
                            <td className="px-3 py-2 text-slate-800">{row?.etd ? toDisplayDate(row.etd) : "-"}</td>
                            <td className="px-3 py-2 text-slate-800">{row?.eta ? toDisplayDate(row.eta) : "-"}</td>
                            <td className="px-3 py-2 text-slate-800">{row?.arrival_basis || "-"}</td>
                            <td className="px-3 py-2 text-slate-800">{row?.arrival_date ? toDisplayDate(row.arrival_date) : "-"}</td>
                            <td className="px-3 py-2 text-right font-mono text-slate-800">{formatQtyValue(row?.pieces)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={11} className="px-3 py-6 text-center text-slate-500">
                            No breakdown rows.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ========================= 子元件 ========================= */

function MultiSelectDropdown({
  value = [],
  options = [],
  onChange,
  placeholder = "All",
  buttonClassName = "",
  showSelectAll = false,
  showSearch = false,
}) {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedValues = toArray(value);
  const labelMap = useMemo(() => {
    const map = new Map();
    options.forEach((option) => {
      if (!option) return;
      const val = option.value;
      if (val === undefined || val === null) return;
      map.set(val, option.label ?? option.value ?? "");
    });
    return map;
  }, [options]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const optionOrder = useMemo(() => options.map((option) => option.value), [options]);
  const filteredOptions = useMemo(() => {
    if (!showSearch) return options;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => {
      const label = String(option?.label ?? option?.value ?? "").toLowerCase();
      return label.includes(term);
    });
  }, [options, searchTerm, showSearch]);
  const hasOptions = optionOrder.length > 0;
  const allSelected = hasOptions && optionOrder.every((optionValue) => selectedValues.includes(optionValue));
  const buildNextList = (nextSet) => {
    const ordered = optionOrder.filter((value) => nextSet.has(value));
    selectedValues.forEach((value) => {
      if (!nextSet.has(value)) return;
      if (optionOrder.includes(value)) return;
      ordered.push(value);
    });
    return ordered;
  };

  const toggleOption = (optionValue) => {
    const nextSet = new Set(selectedValues);
    if (nextSet.has(optionValue)) nextSet.delete(optionValue);
    else nextSet.add(optionValue);
    const nextList = buildNextList(nextSet);
    onChange?.(nextList);
  };

  const clearSelection = () => {
    if (selectedValues.length === 0) return;
    onChange?.([]);
  };
  const selectAll = () => {
    if (!hasOptions || allSelected) return;
    onChange?.(optionOrder.slice());
  };

  const labels = selectedValues.map((val) => labelMap.get(val) || String(val || ""));
  let summary = placeholder;
  if (labels.length === 1) summary = labels[0] || placeholder;
  else if (labels.length === 2) summary = labels.join(", ");
  else if (labels.length > 2) summary = `${labels.length} selected`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className={`flex w-full items-center justify-between gap-2 rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs shadow-sm hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${buttonClassName}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-slate-700">
          {summary || placeholder}
        </span>
        <svg
          className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 8l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-3 py-1.5 text-xs">
            {showSelectAll ? (
              <button
                type="button"
                onClick={selectAll}
                className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                disabled={!hasOptions || allSelected}
              >
                Select All
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={clearSelection}
                className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                disabled={selectedValues.length === 0}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-blue-600 hover:underline"
              >
                Close
              </button>
            </div>
          </div>
          {showSearch && (
            <div className="border-b border-slate-200 px-3 py-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search..."
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}
          <div className="max-h-72 overflow-y-auto py-1">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const val = option.value;
                const label = option.label ?? option.value ?? "";
                const checked = selectedValues.includes(val);
                return (
                  <label
                    key={val}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-blue-600"
                      checked={checked}
                      onChange={() => toggleOption(val)}
                    />
                    <span className="truncate">{label || "(Blank)"}</span>
                  </label>
                );
              })
            ) : (
              <div className="px-3 py-2 text-xs text-slate-500">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CheckboxCell({ value = false, readOnly = false, onChange }) {
  return (
    <div className="flex items-center justify-center">
      <input
        type="checkbox"
        className="h-4 w-4"
        checked={toBool(value)}
        disabled={readOnly}
        onChange={readOnly ? undefined : (e) => onChange(e.target.checked)}
      />
    </div>
  );
}

// 下拉編輯格（Drayage/Warehouse）
function SelectCell({ value, options = [], readOnly = false, placeholder = "", onChange, onBlur }) {
  const baseCls = "w-full border rounded px-2 py-1 text-sm";
  const roCls   = "bg-gray-50 text-gray-500 cursor-not-allowed";
  return (
    <select
      className={readOnly ? `${baseCls} ${roCls}` : baseCls}
      disabled={readOnly}
      value={value || ""}
      onChange={readOnly ? undefined : (e) => onChange(e.target.value)}
      onBlur={readOnly ? undefined : onBlur}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

/** 可編輯文字格 */
function Cell({ value, col, readOnly = false, onChange, onBlur }) {
  const display = formatForDisplay(value, col);
  const [v, setV] = useState(display);
  useEffect(() => setV(display), [display]);

  const baseCls = "w-full border rounded px-2 py-1 text-sm";
  const roCls   = "bg-gray-50 text-gray-500 cursor-not-allowed";

  return (
    <input
      className={readOnly ? `${baseCls} ${roCls}` : baseCls}
      value={v ?? ""}
      disabled={readOnly}
      tabIndex={readOnly ? -1 : 0}
      title={readOnly ? "Read only for your role" : ""}
      onChange={(e) => {
        if (readOnly) return;
        setV(e.target.value);
        onChange(e.target.value);
      }}
      onBlur={readOnly ? undefined : onBlur}
      placeholder={
        col.type === "datetime" ? "mm/dd/yy hh:mm am/pm" :
        col.type === "date" ? "mm/dd/yy" : ""
      }
    />
  );
}

/** 顯示用格式化（ISO -> UI） */
function formatForDisplay(val, col) {
  if (col.type === "boolean") return toBool(val) ? "Yes" : "No";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (val == null || val === "") return "";
  if (col.type === "date") {
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(val);
    return iso ? toDisplayDate(val) : String(val);
  }
  if (col.type === "datetime") {
    const isoDT = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(val);
    return isoDT ? toDisplayDateTime(val) : String(val);
  }
  return String(val);
}

/** 匯入正規化 */
function normalizeCellForImport(cell, col) {
  if (col.type === "boolean") return toBool(cell);
  if (cell == null) return "";
  const s = String(cell).trim();
  if (col.type === "date")     return normalizeDateInput(s, false) || "";
  if (col.type === "datetime") return normalizeDateInput(s, true)  || "";
  return s;
}
